import React, { useEffect, useMemo, useRef, useState } from "react";
import { FN_NAMES, KEYWORDS } from "@/lib/functionLibrary";
import { readFieldDrag } from "@/lib/fieldWells";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Human label for the inferred result type: dtype (row-level sample) or result_type (measure).
export function resultKind(status) {
  const rt = status?.resultType;
  if (rt && rt !== "unknown") return rt;
  const dt = String(status?.dtype || "").toLowerCase();
  if (!dt || dt === "unknown") return null;
  if (/int|float|number|decimal/.test(dt)) return "number";
  if (/bool/.test(dt)) return "boolean";
  if (/date|time/.test(dt)) return "date";
  if (/str|object|category|text/.test(dt)) return "text";
  return null;
}
const resultLabel = (status, mode) => {
  const k = resultKind(status);
  if (!k) return status?.dtype || "";
  if (mode === "measure") return { number: "number", text: "text label", date: "date", boolean: "true/false" }[k] || k;
  return { number: "number → Measure", text: "text → Dimension", date: "date → Dimension", boolean: "true/false → Dimension" }[k] || k;
};

// Token highlighter: refs ([x] / `x`), functions, numbers, strings, operators.
function highlight(src, mode) {
  const fns = (FN_NAMES[mode] || []).map((f) => f.toUpperCase());
  const pattern = /(\[[^\]\n]*\]?)|(`[^`\n]*`?)|("(?:[^"\\]|\\.)*"?|'(?:[^'\\]|\\.)*'?)|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_][A-Za-z0-9_]*\b)|([+\-*/%(),<>=!&|~^])/g;
  let out = "", last = 0, m;
  while ((m = pattern.exec(src)) !== null) {
    out += esc(src.slice(last, m.index));
    const [tok, ref, btick, str, num, word, op] = m;
    if (ref) out += `<span class="xp-ref">${esc(tok)}</span>`;
    else if (btick) out += `<span class="xp-ref">${esc(tok)}</span>`;
    else if (str) out += `<span class="xp-str">${esc(tok)}</span>`;
    else if (num) out += `<span class="xp-num">${esc(tok)}</span>`;
    else if (word) out += fns.includes(word.toUpperCase()) ? `<span class="xp-fn">${esc(tok)}</span>` : `<span class="xp-word">${esc(tok)}</span>`;
    else if (op) out += `<span class="xp-op">${esc(tok)}</span>`;
    else out += esc(tok);
    last = m.index + tok.length;
  }
  out += esc(src.slice(last));
  return out + "\n";
}

// Professional expression editor: overlay syntax highlighting, autocomplete
// (fields + functions), drag-drop field insertion, debounced live validation.
// mode: "row" (pandas columns via `backticks`) | "measure" ([measure_key] refs)
export function ExpressionEditor({ value, onChange, mode = "row", suggestions = [], validate, placeholder, minRows = 4, testid = "expression-editor", apiRef, onStatusChange }) {
  const taRef = useRef(null);
  const preRef = useRef(null);
  const [ac, setAc] = useState(null);          // {items, index, tokenStart}
  const [status, setStatusRaw] = useState(null);  // {state: 'ok'|'error'|'checking', ...}
  const statusCb = useRef(onStatusChange);
  statusCb.current = onStatusChange;
  const setStatus = (s) => { setStatusRaw(s); statusCb.current && statusCb.current(s); };
  const debRef = useRef(null);

  const syncScroll = () => {
    if (preRef.current && taRef.current) {
      preRef.current.scrollTop = taRef.current.scrollTop;
      preRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  };

  // ---- Live validation (debounced) ----
  useEffect(() => {
    if (!validate) return;
    if (!value?.trim()) { setStatus(null); return; }
    setStatus({ state: "checking" });
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        const res = await validate(value);
        setStatus(res.ok
          ? { state: "ok", samples: res.samples, dtype: res.dtype, value: res.value, resultType: res.result_type, note: res.note }
          : { state: "error", message: res.error?.message || res.error || "Invalid expression", guidance: res.guidance || res.error?.guidance, position: res.position ?? res.error?.position, kind: res.kind });
      } catch (e) { setStatus({ state: "error", message: e?.response?.data?.detail || "Validation failed — check your connection." }); }
    }, 650);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Autocomplete ----
  const openAutocomplete = (src, caret) => {
    let start = caret;
    while (start > 0 && /[\w[`]/.test(src[start - 1])) start--;
    const token = src.slice(start, caret);
    if (!token) { setAc(null); return; }
    const tl = token.replace(/^[[`]/, "").toLowerCase();
    const wantRef = token.startsWith("[") || token.startsWith("`");
    const fieldItems = suggestions
      .filter((s) => !tl || s.label.toLowerCase().includes(tl) || (s.value || "").toLowerCase().includes(tl))
      .slice(0, 8)
      .map((s) => ({ kind: s.kind || "field", label: s.label, detail: s.detail, insert: `[${mode === "measure" ? s.value : s.label}]` }));
    const fnItems = wantRef ? [] : (FN_NAMES[mode] || [])
      .filter((f) => tl && f.toLowerCase().startsWith(tl))
      .map((f) => ({ kind: KEYWORDS.has(f) ? "kw" : "fn", label: f, detail: KEYWORDS.has(f) ? "keyword" : "function", insert: KEYWORDS.has(f) ? `${f} ` : `${f}()` }));
    const items = [...fieldItems, ...fnItems].slice(0, 10);
    setAc(items.length ? { items, index: 0, tokenStart: start } : null);
  };

  const applyAc = (item) => {
    const ta = taRef.current;
    const caret = ta.selectionStart;
    const next = value.slice(0, ac.tokenStart) + item.insert + value.slice(caret);
    onChange(next);
    setAc(null);
    requestAnimationFrame(() => {
      const pos = ac.tokenStart + item.insert.length - (item.insert.endsWith(")") ? 1 : 0);
      ta.focus(); ta.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e) => {
    if (ac) {
      if (e.key === "ArrowDown") { e.preventDefault(); setAc((a) => ({ ...a, index: (a.index + 1) % a.items.length })); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setAc((a) => ({ ...a, index: (a.index - 1 + a.items.length) % a.items.length })); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); applyAc(ac.items[ac.index]); return; }
      if (e.key === "Escape") { setAc(null); return; }
    }
  };

  const onInput = (e) => {
    const src = e.target.value;
    onChange(src);
    openAutocomplete(src, e.target.selectionStart);
  };

  const insertAtCaret = (text) => {
    const ta = taRef.current;
    const s = ta?.selectionStart ?? value.length;
    const epos = ta?.selectionEnd ?? value.length;
    const next = value.slice(0, s) + text + value.slice(epos);
    onChange(next);
    requestAnimationFrame(() => {
      const pos = s + text.length - (text.endsWith(")") ? Math.ceil((text.match(/[ ,]/g) || []).length ? text.length - text.indexOf("(") - 1 : 1) : 0);
      ta?.focus(); ta?.setSelectionRange(pos, pos);
    });
  };

  const onDrop = (e) => {
    const p = readFieldDrag(e);
    if (!p) return;
    e.preventDefault();
    const text = mode === "measure"
      ? (p.kind === "measure" ? `[${p.key}]` : `[${p.field}]`)
      : (/[^\w]/.test(p.field || "") ? `\`${p.field}\`` : (p.field || ""));
    insertAtCaret(text);
  };

  const html = useMemo(() => highlight(value || "", mode), [value, mode]);
  if (apiRef) apiRef.current = { insert: insertAtCaret };
  const rowsStyle = { minHeight: `${minRows * 1.5}em` };

  return (
    <div className="space-y-1.5 relative">
      <div className={cn("relative rounded-xl border bg-[#0F172A] overflow-hidden font-mono text-[12.5px] leading-6",
        status?.state === "error" ? "border-[color:var(--danger)]" : "border-[color:var(--glass-border)] focus-within:border-[color:var(--brand-ocean)]")}>
        <pre ref={preRef} aria-hidden style={rowsStyle}
          className="m-0 p-3 whitespace-pre-wrap break-words text-slate-200 pointer-events-none xp-pre"
          dangerouslySetInnerHTML={{ __html: html }} />
        <textarea ref={taRef} data-testid={testid} value={value} onChange={onInput} onKeyDown={onKeyDown}
          onScroll={syncScroll} onBlur={() => setTimeout(() => setAc(null), 150)}
          onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
          placeholder={placeholder} spellCheck={false} autoComplete="off"
          className="absolute inset-0 w-full h-full p-3 bg-transparent text-transparent caret-white resize-none outline-none whitespace-pre-wrap break-words" />
      </div>
        {ac && (
          <div data-testid="expression-autocomplete" className="absolute left-3 top-full -mt-1 z-50 w-72 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] shadow-xl py-1 max-h-56 overflow-auto">
            {ac.items.map((it, i) => (
              <button key={`${it.kind}-${it.label}`} data-testid="autocomplete-item"
                onMouseDown={(e) => { e.preventDefault(); applyAc(it); }}
                className={cn("w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-2 font-sans", i === ac.index ? "bg-[color:rgba(43,78,255,0.12)]" : "hover:bg-[color:var(--surface-2)]")}>
                <span className={cn("text-[9px] font-semibold px-1 rounded", it.kind === "fn" ? "bg-purple-100 text-purple-700" : it.kind === "kw" ? "bg-amber-100 text-amber-700" : it.kind === "column" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700")}>{it.kind === "fn" ? "ƒ" : it.kind === "kw" ? "kw" : it.kind === "column" ? "col" : "▤"}</span>
                <span className="text-[color:var(--ink)] truncate">{it.label}</span>
                {it.detail && <span className="ml-auto text-[10px] text-[color:var(--ink-3)]">{it.detail}</span>}
              </button>
            ))}
          </div>
        )}
      {status && (
        <div data-testid="expression-status" className={cn("text-[11px] flex items-start gap-1.5 px-1",
          status.state === "ok" ? "text-emerald-600" : status.state === "error" ? "text-[color:var(--danger)]" : "text-[color:var(--ink-3)]")}>
          {status.state === "checking" && <><Loader2 className="w-3 h-3 animate-spin mt-0.5" />Validating…</>}
          {status.state === "ok" && (
            <><CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
              <span data-testid="expression-status-ok">Valid{resultLabel(status, mode) ? ` · ${resultLabel(status, mode)}` : ""}{status.value != null ? ` · result: ${typeof status.value === "number" ? status.value.toLocaleString() : String(status.value)}` : ""}{status.samples?.length ? ` · sample: ${status.samples.slice(0, 3).map((v) => String(v)).join(", ")}` : ""}{status.note ? ` · ${status.note}` : ""}</span></>
          )}
          {status.state === "error" && (
            <><AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                {status.message}{status.position != null && <span className="text-[color:var(--ink-3)]"> (near character {status.position + 1})</span>}
                {status.guidance && <span className="block text-[color:var(--ink-3)] mt-0.5">{status.guidance}</span>}
              </span></>
          )}
        </div>
      )}
    </div>
  );
}
